import {useEffect, useState} from 'react';

import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {useEditor, EditorContent, Content} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import {Box, Grid} from '@mui/material';

import {RichTextEditorToolbar} from './RichTextEditorToolbar';
import {orangeColors} from '../../layouts/theme/designSystem';

interface Props {
  defaultText?: Content;
  hideToolbar?: boolean;
  isEditable: boolean;
  readText?: (text: string) => void;
  onCopy?: () => void;
  fontSize?: string;
}

export const RichTextEditor = ({defaultText, hideToolbar, isEditable, readText, onCopy, fontSize = '14px'}: Props) => {
  const [hover, setHover] = useState(false);

  const ProseMirrorOverrideStyles = `
  .ProseMirror {
    font-size: ${fontSize};
  }
  .ProseMirror:focus {
    outline: none;
  }
  .ProseMirror a {
    color: ${orangeColors[700]};
    font-weight: 700;
  }
`;

  const editor = useEditor(
    {
      content: defaultText || '',
      autofocus: false,
      editable: isEditable,
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Underline,
      ],
      onUpdate: ({editor}) => {
        if (readText) {
          readText(editor.getHTML());
        }
      },
      onCreate: ({editor}) => {
        if (readText) {
          readText(editor.getHTML());
        }
      },
    },
    []
  );

  useEffect(() => {
    if (!editor || defaultText === undefined) return;
    // Only update if content is different to avoid resetting cursor unnecessarily
    if (editor.getHTML() !== (typeof defaultText === 'string' ? defaultText : '')) {
      editor.commands.setContent(defaultText);
    }
  }, [defaultText, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
    }
  }, [isEditable, editor]);

  const onCopyHandler = () => {
    if (onCopy) {
      onCopy();
    }
  };

  const getBorderColor = (): string => {
    return hover ? orangeColors[4] : orangeColors[3];
  };

  if (!editor) {
    return null;
  }

  return (
    <Grid onCopy={onCopyHandler} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {!hideToolbar && <RichTextEditorToolbar editor={editor} borderColor={getBorderColor()} />}
      <Box padding='8px' marginTop='16px' borderRadius='4px' border={`1px solid ${getBorderColor()}`}>
        <EditorContent editor={editor} />
      </Box>
      <style>{ProseMirrorOverrideStyles}</style>
    </Grid>
  );
};
